import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { PageHero } from "@/components/layout/PageHero";
import { heroFor } from "@/data/heroes";
import { supabase } from "@/integrations/supabase/client";
import { pageBlocks } from "@/lib/content";

const TITLE = "Blog — Light of Life International";
const DESCRIPTION =
  "Field reports, testimonies and ministry updates from Light of Life International crusades and outreaches.";

export const Route = createFileRoute("/blog")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://loli.ardent.dev/blog" },
    ],
    links: [{ rel: "canonical", href: "https://loli.ardent.dev/blog" }],
  }),
  component: BlogPage,
});

function BlogPage() {
  const blocks = pageBlocks("blog");
  const text = (i: number) => {
    const b = blocks[i];
    return b && "text" in b ? b.text : "";
  };
  const [query, setQuery] = useState("");

  const { data: posts, isLoading } = useQuery({
    queryKey: ["published-posts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posts")
        .select(
          "id, slug, title, excerpt, published_at, media:featured_media_id(public_url, alt_text)",
        )
        .eq("status", "published")
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const filtered = useMemo(() => {
    if (!posts) return [];
    const q = query.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter((p) => `${p.title} ${p.excerpt ?? ""}`.toLowerCase().includes(q));
  }, [posts, query]);

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <Header overlay />

      <PageHero
        title="Blog"
        image={heroFor("/blog").image}
        subtitle={text(1)}
        attribution={text(2)}
      />

      <main id="main" className="flex-1">
        <section className="section-y">
          <div className="container-site">
            <h2 className="text-center text-4xl sm:text-5xl">
              <span className="text-muted-foreground">Latest </span>
              <span className="text-accent">Posts</span>
            </h2>

            <div className="mx-auto mt-10 max-w-4xl">
              <label className="relative block">
                <span className="sr-only">Search posts</span>
                <svg
                  aria-hidden
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Enter keywords..."
                  className="h-14 w-full rounded-md bg-muted pl-14 pr-5 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                />
              </label>
            </div>

            <div className="mt-14">
              {isLoading ? (
                <p className="text-center text-muted-foreground">Loading posts…</p>
              ) : filtered.length > 0 ? (
                <ul className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
                  {filtered.map((post) => (
                    <li key={post.id} className="flex flex-col">
                      {post.media?.public_url ? (
                        <Link to="/blog/$slug" params={{ slug: post.slug }}>
                          <img
                            src={post.media.public_url}
                            alt={post.media.alt_text ?? ""}
                            loading="lazy"
                            decoding="async"
                            className="h-56 w-full rounded-lg object-cover"
                          />
                        </Link>
                      ) : null}
                      <h3 className="mt-6 text-2xl text-navy">
                        <Link to="/blog/$slug" params={{ slug: post.slug }}>
                          {post.title}
                        </Link>
                      </h3>
                      {post.published_at ? (
                        <p className="eyebrow mt-2 text-xs text-muted-foreground">
                          {new Date(post.published_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                      ) : null}
                      {post.excerpt ? <p className="mt-3 text-base">{post.excerpt}</p> : null}
                      <Link
                        to="/blog/$slug"
                        params={{ slug: post.slug }}
                        className="mt-4 font-heading text-sm uppercase tracking-widest text-brand-foreground underline decoration-accent decoration-2 underline-offset-4"
                      >
                        Read the full report: {post.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mx-auto max-w-2xl text-center text-lg text-muted-foreground">
                  {query
                    ? "No posts match your search."
                    : "Ministry updates and field reports will appear here soon."}
                </p>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
